import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { Reveal } from "@/components/Reveal";

export default function BlogPage() {
  const { t } = useLang();
  const { posts, L } = useContent();

  useEffect(() => {
    document.title = `${t.blog.title} — Bezalel Visionary`;
    window.scrollTo(0, 0);
  }, [t]);

  return (
    <main data-testid="blog-page">
      <Navbar />
      <div className="bg-[#F7F3EC] pt-32 lg:pt-40 pb-20 lg:pb-32 min-h-screen">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <p className="eyebrow-label mb-6">{t.blog.eyebrow}</p>
          <h1 className="font-serif text-[#191713] text-4xl sm:text-5xl">{t.blog.title}</h1>
          <p className="mt-4 text-[#191713]/60 max-w-xl">{t.blog.sub}</p>
          <div className="mt-14 space-y-10">
            {posts.length === 0 && <p className="text-sm text-[#8A8378]">{t.blog.empty}</p>}
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                  className="group grid grid-cols-1 sm:grid-cols-12 gap-6 border-b border-[#191713]/10 pb-10">
                  {p.cover_image && (
                    <div className="sm:col-span-4 overflow-hidden bg-[#E9E0D0]">
                      <img src={p.cover_image} alt={L(p.title)} loading="lazy"
                        className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]" />
                    </div>
                  )}
                  <div className={p.cover_image ? "sm:col-span-8" : "sm:col-span-12"}>
                    <p className="text-xs text-[#8A8378]">{new Date(p.created_at).toLocaleDateString()}</p>
                    <h2 className="mt-2 font-serif text-2xl text-[#191713] group-hover:text-[#A6853B] transition-colors">{L(p.title)}</h2>
                    <p className="mt-3 text-sm text-[#191713]/65 leading-relaxed max-w-2xl">{L(p.excerpt)}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#A6853B]">
                      {t.blog.readMore} <ArrowRight size={13} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const { t } = useLang();
  const { posts, L } = useContent();
  const post = posts.find((p) => p.slug === slug);

  useEffect(() => {
    if (post) {
      document.title = `${L(post.title)} — Bezalel Visionary`;
      const desc = L(post.excerpt);
      if (desc) document.querySelector('meta[name="description"]')?.setAttribute("content", desc);
    }
    window.scrollTo(0, 0);
  }, [post]); // eslint-disable-line

  return (
    <main data-testid="blog-post-page">
      <Navbar />
      <div className="bg-[#F7F3EC] pt-28 lg:pt-40 pb-20 lg:pb-28 min-h-screen">
        <article className="max-w-3xl mx-auto px-6 lg:px-10">
          <Link to="/blog" data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#8A8378] hover:text-[#A6853B] transition-colors">
            <ArrowLeft size={15} /> {t.blog.back}
          </Link>
          {!post ? (
            <p className="mt-10 text-sm text-[#8A8378]">{t.blog.empty}</p>
          ) : (
            <>
              <p className="mt-10 text-xs text-[#8A8378]">{new Date(post.created_at).toLocaleDateString()}</p>
              <h1 className="mt-3 font-serif text-[#191713] text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="blog-post-title">{L(post.title)}</h1>
              {post.cover_image && (
                <img src={post.cover_image} alt={L(post.title)} className="mt-8 w-full h-auto border border-[#191713]/10" />
              )}
              <div className="mt-8 space-y-5">
                {L(post.body).split(/\n\s*\n/).filter(Boolean).map((para, i) => (
                  <p key={i} className="text-[#191713]/75 leading-relaxed">{para}</p>
                ))}
              </div>
              <div className="mt-14 bg-[#191713] px-8 py-10 text-center">
                <h2 className="font-serif text-2xl text-[#F7F3EC]">{t.finalCta.title}</h2>
                <Link to="/contact" data-testid="blog-cta-contact" className="btn-gold mt-6">
                  {t.finalCta.cta1} <ArrowRight size={16} />
                </Link>
              </div>
            </>
          )}
        </article>
      </div>
      <Footer />
    </main>
  );
}
